import ApiService from "../../../core/services/ApiService";
import { ENV } from "../../../config";
import { DEFAULT_FILTER_KEYS } from "../../../shared/utils/CommonConstants";
import { FilterKeyTypes } from "../../../shared/types/FilterKeys.types";
import { TemplateFilterParams } from "../pages/TemplateList/TemplateList.types";
import { ConditionalFormattingRule } from "../../../shared/screen-components/ExcelViewComponent/ExcelViewComponent.types";

const CLIENT_ID = ENV.CLIENT_ID;

class TemplateService extends ApiService {
  basePath: string = "/api";

  async getAllRuleEngines<T>() {
    return this.makeRequest<T>(`${this.basePath}/rule-engine/template`, {
      method: "get",
    });
  }

  async getAllTemplates<T>(filterParams?: TemplateFilterParams): Promise<T> {
    return this.makeRequest<T>(
      `${this.basePath}/v2/rule-engine/template?client-id=${CLIENT_ID}`,
      {
        method: "post",
        data: filterParams || ({} as any),
        extractData: true,
      },
    );
  }

  async getTemplates<T>(filterParams: TemplateFilterParams): Promise<T> {
    return this.makeRequest<T>(`${this.basePath}/rule-engine/template`, {
      method: "POST",
      data: filterParams as any,
      extractData: true,
    });
  }

  async templatesFilter<T>(): Promise<T> {
    return this.makeRequest<T>(
      `${this.basePath}/rule-engine-template/filter-options?client-id=${CLIENT_ID}`,
      {
        method: "get",
        extractData: true,
      },
    );
  }

  async createTemplate<T>(templateName: string, file: File): Promise<T> {
    const formData = new FormData();
    formData.append("templateName", templateName);
    formData.append("file", file);
    return this.makeRequest<T>(`${this.basePath}/rule-engine`, {
      method: "post",
      data: formData,
      extractData: true,
    });
  }

  async getTemplateMenuDetails<T>(id: number) {
    return this.makeRequest<T>(`${this.basePath}/rule-engine/${id}/sheets`, {
      method: "get",
    });
  }

  async getTemplateSheetsData<T>(id: number, sheetId: number) {
    return this.makeRequest<T>(
      `${this.basePath}/rule-engine/${id}/sheet/${sheetId}`,
      { method: "get" },
    );
  }

  async getTemplateData<T>(
    templateId: number,
    sheetId: number,
    startIndex = 0,
    rowSize = 50,
    pageNumber?: string,
    filteredKeys: FilterKeyTypes = { ...DEFAULT_FILTER_KEYS },
  ) {
    return this.makeRequest<T>(
      `${this.basePath}/rule-engine/${templateId}/sheet/${sheetId}?start-index=${
        startIndex > 0 ? startIndex : 0
      }&row-size=${rowSize}&sheet-number=${pageNumber ?? ""}&key1=${
        filteredKeys.Key1
      }&key2=${filteredKeys.Key2}&key3=${filteredKeys.Key3}&key4=${
        filteredKeys.Key4
      }`,
      { method: "get" },
    );
  }

  async getRuleEngineTemplateUsingAxios(
    deliverableId: number,
    isJson: boolean = false,
  ) {
    return this.makeRequest<Blob>(
      `${this.basePath}/rule-engine/${deliverableId}/sheet/-1/template`,
      {
        method: "GET",
        responseType: "blob",
        params: { "is-json": isJson },
      },
    );
  }

  async getRuleEngineConditionalFormattingUsingAxios(
    deliverableId: number,
    sheetId: number,
  ): Promise<ConditionalFormattingRule[]> {
    return this.makeRequest<ConditionalFormattingRule[]>(
      `${this.basePath}/rule-engine/${deliverableId}/sheet/${sheetId}/template-conditional-format`,
      { extractData: true },
    );
  }

  async importTemplate<T>(id: string, file: File, startIndex: number = 0): Promise<T> {
    const formData = new FormData();
    formData.append("file", file);
    return this.makeRequest(
      `${this.basePath}/rule-engine/${id}/template/upload`,
      {
        method: "post",
        data: formData,
        extractData: true,
      },
    );
  }

  async exportTemplateAxios<T>(
    id: string,
    startIndex: number = 0,
    rowSize: number = 10,
    filteredKeys: FilterKeyTypes = { ...DEFAULT_FILTER_KEYS },
    sheetNumber?: string,
  ): Promise<T> {
    return this.makeRequest<T>(
      `${this.basePath}/rule-engine/${id}/template/export`,
      {
        responseType: "arraybuffer",
        params: {
          "start-index": startIndex < 1 ? 0 : startIndex,
          "row-size": rowSize,
          "keys.key1": filteredKeys.Key1,
          "keys.key2": filteredKeys.Key2,
          "keys.key3": filteredKeys.Key3,
          "keys.key4": filteredKeys.Key4,
          "sheet-number": sheetNumber,
        },
        extractData: true,
      },
    );
  }
}

export default TemplateService;
